import {ListFilterSelectOption, MaterialType, StatusChipColors} from '@aviary/store/common/common.types';
import {SearchSubheaderQueryTypes} from '@aviary/components/search/search.types';
import {Params} from '@angular/router';

export const CATALOG_FEATURE_KEY = 'catalog';

export interface CatalogRecord {
  readonly id: number;
  readonly organizationId: number;
  readonly marcRecord: string;
  readonly title: string;
  readonly description: string;
  readonly author: string;
  readonly physicalDescription: string;
  readonly identifier: string;
  readonly identifierType: IdentifierType;
  readonly imageUrl: string;
  readonly materialType: MaterialType;
  readonly availability: CatalogRecordAvailability;
  readonly marcDetails: MarcDetails;
  readonly patronFavorite?: boolean;
  readonly fromIll: boolean;
}

export interface CatalogRecordAvailabilityDetail {
  readonly branchAvailability: CatalogRecordAvailabilityBranch[];
  readonly totals: CatalogRecordAvailability;
}

export interface CatalogRecordAvailability {
  readonly availableCount: number; // circulating, loanable, and status is IN_LIBRARY or SHELVING
  readonly onHoldCount: number;
  readonly totalCopies: number; // literally all active copies
  readonly opacVisibleCopies: number;
  readonly circulatingCopies: number; // circulating, loanable, and status is IN_LIBRARY, CHECKED_OUT, IN_TRANSFER, HELD, SHELVING, IN_PROCESS, or ON_ORDER
  readonly holdsInQueue: number;
  readonly availableAtPatronBranch: number;
  readonly callNumber: string;
  readonly collectionName: string;
}

export interface CatalogRecordAvailabilityBranch {
  readonly patronHomeBranch: boolean;
  readonly availableCount: number;
  readonly opacVisibleCopies: number;
  readonly branchId: number;
  readonly branchName: string;
  readonly circulationItems: CirculationItem[];
}

export type CirculationItemStatus = 'CHECKED_OUT' | 'IN_TRANSFER' | 'IN_LIBRARY' | 'HELD' |
  'LOST' | 'MISSING' | 'WITHDRAWN' | 'CLAIM_RETURNED' | 'CLAIM_NEVER_HAD' | 'CLAIM_MISSING_PARTS' |
  'SHELVING' | 'BINDERY' | 'IN_REPAIR' | 'UNAVAILABLE' | 'IN_PROCESS' | 'ON_ORDER' | 'DAMAGED';

export const DISPLAYABLE_CIRCULATION_ITEM_STATUSES: Map<CirculationItemStatus, string> = new Map([
  ['CHECKED_OUT', 'Checked Out'],
  ['IN_TRANSFER', 'In Transit'],
  ['IN_LIBRARY', 'Available'],
  ['HELD', 'Held'],
  ['LOST', 'Lost'],
  ['MISSING', 'Missing'],
  ['WITHDRAWN', 'Withdrawn'],
  ['CLAIM_RETURNED', 'Claimed Returned'],
  ['CLAIM_NEVER_HAD', 'Claimed Never Had'],
  ['CLAIM_MISSING_PARTS', 'Missing Parts'],
  ['SHELVING', 'Shelving'],
  ['BINDERY', 'Bindery'],
  ['IN_REPAIR', 'In Repair'],
  ['UNAVAILABLE', 'Unavailable'],
  ['IN_PROCESS', 'In Process'],
  ['ON_ORDER', 'On Order'],
  ['DAMAGED', 'Damaged']
]);

export const CIRCULATION_ITEM_STATUS_CHIP_COLORS: StatusChipColors<CirculationItemStatus> = {
  'status-positive': ['IN_LIBRARY'],
  'status-neutral': ['SHELVING', 'BINDERY', 'ON_ORDER'],
  'status-negative': ['CHECKED_OUT', 'LOST', 'MISSING'],
  'status-transitional': ['HELD', 'IN_TRANSFER', 'IN_REPAIR', 'IN_PROCESS', 'CLAIM_RETURNED', 'CLAIM_NEVER_HAD', 'CLAIM_MISSING_PARTS'],
  'status-inactive': ['WITHDRAWN', 'UNAVAILABLE', 'DAMAGED']
};

export interface CirculationItem {
  readonly barcode: string;
  readonly callNumber: string;
  readonly shelfName: string;
  readonly status: CirculationItemStatus;
  readonly tempLocation: string;
  readonly currentBranchId: number;
  readonly homeBranchId: number;
  readonly assignedBranchId: number;
  readonly collectionName: string;
  readonly collectionAbbreviation: string;
  readonly fromIll: boolean;
}

export type IdentifierType = 'ISBN' | 'ISSN' | 'UPC' | 'EAN';

export interface MarcDetails {
  readonly title: string;
  readonly subtitle: string;
  readonly statementOfResponsibility: string;
  readonly author: string;
  readonly otherAuthors: string[];
  readonly summary: string;
  readonly languageCode: string;
  readonly physicalDescription: string;
  readonly publisher: string;
  readonly publicationDate: string;
  readonly publicationLocation: string;
  readonly edition: string;
  readonly subjects: string[];
  readonly genres: string[];
  readonly notes: string[];
  readonly callNumbers: { [key: string]: string };
  readonly identifiers: { [key: string]: string[] };
  readonly controlNumbers: { [key: string]: string };
  readonly marcRecordStrings: string[];
  readonly clean: CleanedMarcDetails;
  readonly urls?: string[];
  readonly seriesString: string;
}

export interface CleanedMarcDetails {
  readonly publicationDate: string;
}

export interface CarouselDisplay {
  readonly id: number,
  readonly title: string,
  readonly displayOrder: number,
  readonly catalogRecords: CatalogRecord[]
}

export const CATALOG_SEARCH_QUERY_TYPES: SearchSubheaderQueryTypes = {
  KEYWORD: 'Keyword',
  AUTHOR: 'Author',
  TITLE: 'Title',
  ISBN: 'ISBN',
};

export const CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX = 'f.' as const;
export const CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX_LENGTH = CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX.length;
export type CatalogSearchFilterQueryParamName = `${typeof CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX}${string}`;

export interface SeparatedSearchParams {
  filters: Record<string, string[]>;
  otherQueryParams: Params;
}

export interface CatalogSearchFilterSelectOption extends ListFilterSelectOption {
  selected: boolean;
}

export type CatalogSearchFilterSelectOptions = CatalogSearchFilterSelectOption[];

export interface CatalogSearchFilterOptionChangedEvent {
  value: string;
  selected: boolean;
}

export type CatalogRecordOrID = CatalogRecord | number;

export interface CatalogSearchFilterEntry {
  filterName: string;
  cardTitle: string;
}

export const OrderedCatalogSearchFilterEntries: CatalogSearchFilterEntry[] = [
  {filterName: "branchIds", cardTitle: "Branches"},
  {filterName: "materialTypeIds", cardTitle: "Format"},
  {filterName: "collectionIds", cardTitle: "Collections"},
  {filterName: "genres", cardTitle: "Genre"},
  {filterName: "subjects", cardTitle: "Subject"},
  {filterName: "literaryForms", cardTitle: "Literary Form"},
  {filterName: "targetAudiences", cardTitle: "Target Audience"},
  {filterName: "publicationDates", cardTitle: "Published Date"}
];

export interface FilterChipEntry {
  filterName: string;
  displayName: string;
  value: string;
}
